/* lopettia-auth client — used by both AI Board and La99.
 *
 * All calls are same-origin (Vite proxies /api → :9100) so the
 * `lopettia_session` cookie is automatically attached. Default `credentials`
 * is set to "include" anyway as belt-and-suspenders for prod where the
 * frontend may be served from a different origin behind a CDN.
 */

export const PRODUCT = 'aiboard';  // matches services/tiers.py products key

export interface User {
  id: string;
  email: string;
  display_name?: string | null;
  role: 'user' | 'admin';
  email_verified: boolean;
  subscription_tier: 'free' | 'pro' | 'team';
  subscription_status: 'none' | 'trial' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  subscription_active_until: string | null;
  products: Record<string, string[]>;
}

export interface Subscription {
  tier: User['subscription_tier'];
  status: User['subscription_status'];
  active_until: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface TierInfo {
  key: string;
  label: string;
  tagline: string;
  price_monthly: number;
  price_yearly: number;
  trial_days: number;
  products: Record<string, string[]>;
}

export class AuthError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail: unknown = null) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function api<T = any>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init.headers || {}),
    },
    ...init,
  });
  const text = await res.text();
  let body: any = null;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && (body.detail?.message || body.detail)) ||
      (typeof body === 'string' ? body : `HTTP ${res.status}`);
    throw new AuthError(res.status, String(message), body);
  }
  return body as T;
}

// ===== Auth =====

export function register(payload: {
  email: string;
  password: string;
  display_name?: string;
  accept_terms: boolean;
}): Promise<User> {
  return api<User>('/api/auth/register', { method: 'POST', body: JSON.stringify(payload) });
}

export function login(payload: { email: string; password: string }): Promise<User> {
  return api<User>('/api/auth/login', { method: 'POST', body: JSON.stringify(payload) });
}

export function logout(): Promise<{ ok: true }> {
  return api('/api/auth/logout', { method: 'POST' });
}

export function me(): Promise<User> {
  return api<User>('/api/auth/me');
}

export function verifyEmail(token: string): Promise<{ ok: boolean; already_verified: boolean; tier?: string; trial_until?: string }> {
  return api(`/api/auth/verify-email?token=${encodeURIComponent(token)}`);
}

export function resendVerification(): Promise<{ ok: boolean; sent?: boolean; already_verified?: boolean }> {
  return api('/api/auth/verify-email/resend', { method: 'POST' });
}

export function forgotPassword(email: string): Promise<{ ok: true; message: string }> {
  return api('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
}

export function resetPassword(token: string, new_password: string): Promise<{ ok: true; message: string }> {
  return api('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, new_password }) });
}

// ===== Billing =====

export function billingMe(): Promise<Subscription> {
  return api<Subscription>('/api/billing/me');
}

export function checkout(tier: 'pro' | 'team', cycle: 'monthly' | 'yearly'): Promise<{ url: string; session_id: string; tier: string; cycle: string; price_usd: number }> {
  return api('/api/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ tier, cycle }),
  });
}

export function billingPortal(): Promise<{ url: string }> {
  return api('/api/billing/portal', { method: 'POST' });
}

export function listTiers(): Promise<{ tiers: TierInfo[] }> {
  return api('/api/tiers');
}

// ===== Account self-service (GDPR — F-06) =====

/** Download a JSON dump of the current user's data (profile + blobs + audits). */
export async function exportMyData(): Promise<void> {
  const res = await fetch('/api/account/export', { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    throw new AuthError(res.status, text || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Try to read the filename from Content-Disposition; fall back to a default.
  const disp = res.headers.get('content-disposition') || '';
  const m = /filename="([^"]+)"/.exec(disp);
  a.download = m ? m[1] : `lopettia-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Soft-delete the current account. The server schedules hard-purge after 30
 *  days; that window is the user's safety net if the request was made under
 *  duress / hijack. */
export function deleteMyAccount(): Promise<{
  ok: boolean;
  soft_deleted_at?: string;
  hard_purge_at?: string;
  already_deleted?: boolean;
}> {
  return api('/api/account/delete', { method: 'POST' });
}

// ===== Helpers =====

export function hasFeature(user: User | null, slug: string, product: string = PRODUCT): boolean {
  if (!user) return false;
  const feats = user.products?.[product] || [];
  return feats.includes(slug);
}

export function trialDaysLeft(user: User | null): number | null {
  if (!user || user.subscription_status !== 'trial' || !user.subscription_active_until) return null;
  const until = new Date(user.subscription_active_until).getTime();
  const now = Date.now();
  const ms = until - now;
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
